/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/mappers.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Mapping utilities for converting between API responses and form state.

────────────────────────────────────────────────────────────────*/

import {
  FormState,
  RingGroupResponse,
  MappedFormState,
  MemberRow,
  ToggleOption,
  ForwardOption,
  MissedCallAction
} from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';
import {
  getStringSetting,
  asToggle,
  asForwardOption,
  asMissedCall,
  extractExtraSettings
} from './settingsHelpers';

/**
 * Map API response to form state
 */
export function mapResponseToState(group: RingGroupResponse): MappedFormState {
  const settings =
    group.settings && typeof group.settings === 'object'
      ? (group.settings as Record<string, unknown>)
      : {};

  const extraSettings = extractExtraSettings(settings);

  const callTimeoutOverride =
    typeof settings.call_timeout_override === 'number'
      ? String(settings.call_timeout_override)
      : typeof settings.call_timeout_override === 'string'
      ? settings.call_timeout_override
      : '';

  const fallbackModule = group.fallbackModule || 'none';
  const fallbackValue = group.fallbackDestination ?? '';

  const ringback = getStringSetting(settings, 'ringback', '${us-ring}') || '${us-ring}';

  const formState: FormState = {
    name: group.name ?? '',
    extension: getStringSetting(settings, 'extension'),
    context: group.context ?? 'default',
    strategy: group.strategy ?? 'simultaneous',
    ringTimeout: String(group.ringTimeout ?? 30),
    fallbackModule,
    fallbackValue,
    callerIdName: getStringSetting(settings, 'cid_name_prefix'),
    callerIdNumber: getStringSetting(settings, 'cid_number_prefix'),
    distinctiveRing: getStringSetting(settings, 'distinctive_ring'),
    ringback,
    greeting: getStringSetting(settings, 'greeting'),
    callScreen: asToggle(settings.call_screen, 'disabled'),
    callForward: asToggle(settings.call_forward, 'disabled'),
    followMe: asToggle(settings.follow_me, 'disabled'),
    missedCallAction: asMissedCall(settings.missed_call_action, 'none'),
    forwardEnabled: asForwardOption(settings.forward_enabled, 'disabled'),
    forwardNumber: getStringSetting(settings, 'forward_number'),
    forwardTollAllow: getStringSetting(settings, 'forward_toll_allow'),
    confirmKey: getStringSetting(settings, 'confirm_key', '1') || '1',
    confirmFile: getStringSetting(settings, 'confirm_file', 'ivr/press_1_to_accept.wav') || 'ivr/press_1_to_accept.wav',
    presenceId: getStringSetting(settings, 'presence_id'),
    record: Boolean(settings.record),
    callTimeoutOverride,
    skipBusy: Boolean(group.skipBusy),
    enabled: Boolean(group.enabled),
    description: group.description ?? '',
    createdAt: group.createdAt ?? null,
    updatedAt: group.updatedAt ?? null
  };

  if (!formState.presenceId) {
    const baseName =
      group.name
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '') ?? '';
    formState.presenceId = baseName ? `${baseName}@${formState.context}` : '';
  }

  const members: MemberRow[] = Array.isArray(group.members)
    ? group.members.map((member, index) => {
        // Preserve id only if it exists and is valid
        const memberId = member?.id && typeof member.id === 'number' && member.id > 0 ? member.id : undefined;
        return {
          id: memberId,
          extension: member?.extension ?? '',
          orderIndex: String(member?.orderIndex ?? index + 1),
          delay: String(member?.delay ?? 0),
          timeout: String(member?.timeout ?? (Number(formState.ringTimeout) || 30)),
          prompt: member?.prompt ?? '',
          description: member?.description ?? '',
          enabled: member?.enabled ?? true
        };
      })
    : [];

  return { formState, members, extraSettings };
}

